import type { RepeatableLogger } from './repeatable-group.types';

export type RepeatablePendingDropdownScheduleReason =
    | 'hydration'
    | 'dataSourceLoaded'
    | 'dependenciesResolved'
    | 'retry';

export interface RepeatablePendingDropdownScheduleOptions {
    readonly delayMs?: number;
    readonly reason?: RepeatablePendingDropdownScheduleReason;
}

export interface RepeatablePendingDropdownScheduleState {
    readonly isScheduled: boolean;
    readonly delayMs: number;
    readonly reason: RepeatablePendingDropdownScheduleReason;
    readonly scheduledAt: number;
}

const DEFAULT_PENDING_DROPDOWN_RETRY_DELAY_MS = 50;

/**
 * Schedules pending dropdown resolution without coupling the component to timer details.
 *
 * Delay zero uses a microtask so freshly loaded catalog options can resolve pending values before
 * falling back to the legacy retry timer. Timed retries remain available as a safety net for
 * asynchronous option sources that do not expose completion events yet.
 */
export class RepeatablePendingDropdownScheduler {
    private timeoutId: ReturnType<typeof setTimeout> | null = null;
    private scheduledState: RepeatablePendingDropdownScheduleState | null = null;

    public constructor(
        private readonly logger: RepeatableLogger,
        private readonly defaultDelayMs = DEFAULT_PENDING_DROPDOWN_RETRY_DELAY_MS,
    ) {}

    public schedule(callback: () => void, options: RepeatablePendingDropdownScheduleOptions = {}): void {
        this.clear();

        const delayMs = this.normalizeDelay(options.delayMs);
        const reason = options.reason ?? 'retry';
        const state: RepeatablePendingDropdownScheduleState = {
            isScheduled: true,
            delayMs,
            reason,
            scheduledAt: Date.now(),
        };

        this.scheduledState = state;

        if (delayMs === 0) {
            void Promise.resolve().then(() => {
                this.runIfCurrent(state, callback);
            });
            this.logger.log('Repeatable pending dropdown resolution scheduled in microtask.', { reason });

            return;
        }

        this.timeoutId = setTimeout(() => {
            this.timeoutId = null;
            this.runIfCurrent(state, callback);
        }, delayMs);

        this.logger.log('Repeatable pending dropdown resolution scheduled.', { delayMs, reason });
    }

    public clear(): void {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }

        this.scheduledState = null;
    }

    public getState(): RepeatablePendingDropdownScheduleState | null {
        return this.scheduledState;
    }

    private normalizeDelay(delayMs: number | undefined): number {
        const candidate = delayMs ?? this.defaultDelayMs;

        return Number.isFinite(candidate) && candidate > 0 ? Math.floor(candidate) : 0;
    }

    private runIfCurrent(state: RepeatablePendingDropdownScheduleState, callback: () => void): void {
        if (this.scheduledState !== state) {
            return;
        }

        this.scheduledState = null;
        callback();
    }
}
